import {
    VuexModule,
    Module,
    getModule,
    Mutation,
    Action,
} from 'vuex-module-decorators';
import store from '@/store';
import { login, getConnectedUser, saveTrackingConnection } from '@/api/api';
import { Role } from '@/models/Role';
import jwt_decode from 'jwt-decode';
import Subscription from '@/models/Subscription';
import UserToken from '@/models/UserToken';

interface JWT {
    iat?: number;
    exp?: number;
}

function isTokenValid(token: string): boolean {
    if (token && token.trim && token.trim()) {
        try {
            const tokenDecoded: JWT = jwt_decode(token);
            const now = Math.floor(new Date().getTime() / 1000);

            if (tokenDecoded.exp) {
                if (tokenDecoded.exp < now) {
                    console.warn('Token expired');
                    return false;
                }
            }
            return true;
        } catch (error) {
            console.warn(error);
            return false;
        }
    }
    return false;
}

// Fonction pour récupérer l'adresse IP de l'utilisateur
async function getUserIP(): Promise<string> {
    try {
        const response = await fetch('https://api.ipify.org?format=json');
        const data = await response.json();
        return data.ip;
    } catch (error) {
        console.warn('Could not fetch user IP:', error);
        return 'Unknown';
    }
}

interface AuthState {
    token: string;
    logged: boolean;
    isKochise: boolean;
    roles: Role[];
    userID: number;
    email: string;
    firstname: string;
    isRestricted: Boolean;
    lastname: string;
    logo: string;
    isTrialActive: boolean | null;
    isSubscriptionActive: boolean | null;
    enabledAt: Date | null;
    subscriptions: Subscription[];
    personal_color: string;
    userTokens: UserToken[];
    crm_url: string;
    // Propriétés pour le tracking (currentTabId supprimé)
    lastLoginIP: string | null;
    sessionDurations: Array<{
        tabId: string;
        startTime: string;
        endTime: string | null;
        duration: number | null;
    }>;
}

interface LoginArg {
    username: string;
    password: string;
}

@Module({ dynamic: true, store, name: 'auth', namespaced: true })
class AuthModule extends VuexModule implements AuthState {
    public token = '';
    public logged = false;
    public roles: Role[] = [];
    public userID: number = -1;
    public email: string = '';
    public firstname: string = '';
    public isRestricted: boolean = false;
    public lastname: string = '';
    public logo: string = '';
    public isTrialActive: boolean | null = null;
    public isSubscriptionActive: boolean | null = null;
    public enabledAt: Date | null = null;
    public subscriptions: Subscription[] = [];
    public isKochise: boolean = false;
    public personal_color: string = '';
    public userTokens: UserToken[] = [];
    public crm_url: string = '';
    
    // Propriétés de tracking (currentTabId supprimé)
    public lastLoginIP: string | null = null;
    public sessionDurations: Array<{
        tabId: string;
        startTime: string;
        endTime: string | null;
        duration: number | null;
    }> = [];

    public initialized = false;

    @Mutation
    setInitialized() {
        this.initialized = true;
    }

    @Mutation
    setCrmUrl(url: string): void {
        this.crm_url = url;
    }

    @Mutation
    setLogged(logged: boolean): void {
        this.logged = logged;
    }

    @Mutation
    setToken(token: string): void {
        this.token = token;
    }

    @Mutation
    setUserID(userID: number) {
        this.userID = userID;
    }

    @Mutation
    setRoles(roles: any) {
        this.roles = roles;
    }
    @Mutation
    setEmail(email: string) {
        this.email = email;
    }
    @Mutation
    setLastname(lastname: string) {
        this.lastname = lastname;
    }
    @Mutation
    setFirstname(firstname: string) {
        this.firstname = firstname;
    }
    @Mutation
    setIsRestricted(isRestricted: boolean) {
        this.isRestricted = isRestricted;
    }
    @Mutation
    setLogo(logo: string) {
        this.logo = logo;
    }
    @Mutation
    setTrialActive(trialActive: boolean | null) {
        this.isTrialActive = trialActive;
    }
    @Mutation
    setSubscriptionActive(subscriptionActive: boolean | null) {
        this.isSubscriptionActive = subscriptionActive;
    }
    @Mutation
    setEnabledAt(enabledAt: Date) {
        this.enabledAt = enabledAt;
    }
    @Mutation
    setIsKochise(isKochise: boolean) {
        this.isKochise = isKochise;
    }
    @Mutation
    setSubscriptions(subscriptions: Subscription[]) {
        this.subscriptions = subscriptions;
    }

    @Mutation
    setPersonalColor(personal_color: string) {
        this.personal_color = personal_color;
    }

    @Mutation
    setUserTokens(userTokens: UserToken[]) {
        this.userTokens = userTokens;
    }

    // Mutations pour le tracking (setCurrentTabId supprimé)
    @Mutation
    setLastLoginIP(ip: string) {
        this.lastLoginIP = ip;
    }

    @Mutation
    addSessionDuration(session: {
        tabId: string;
        startTime: string;
        endTime: string | null;
        duration: number | null;
    }) {
        this.sessionDurations.push(session);
    }

    @Mutation
    updateSessionEnd(payload: { tabId: string; endTime: string; duration: number }) {
        const session = this.sessionDurations.find(s => s.tabId === payload.tabId);
        if (session) {
            session.endTime = payload.endTime;
            session.duration = payload.duration;
        }
    }

    @Mutation
    setSessionDurations(sessions: Array<{
        tabId: string;
        startTime: string;
        endTime: string | null;
        duration: number | null;
    }>) {
        this.sessionDurations = sessions;
    }

    get isSubscribed() {
        return (
            this.isTrialActive !== false ||
            this.isSubscriptionActive !== false ||
            this.roles.includes('ROLE_ADMIN')
        );
    }

    get crmUrl() {
        return this.crm_url
    }

    // Getter pour la session active actuelle (sans endTime)
    get currentSession() {
        return this.sessionDurations.find(s => !s.endTime);
    }

    // Getter pour la durée de session actuelle
    get currentSessionDuration() {
        const currentSession = this.currentSession;
        if (currentSession) {
            const start = new Date(currentSession.startTime);
            const now = new Date();
            const diffMs = now.getTime() - start.getTime();
            
            const hours = Math.floor(diffMs / (1000 * 60 * 60));
            const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);
            
            return {
                totalMs: diffMs,
                hours,
                minutes,
                seconds,
                formatted: `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`,
                startTime: currentSession.startTime,
                tabId: currentSession.tabId
            };
        }
        return null;
    }

    // Getter pour toutes les sessions terminées
    get completedSessions() {
        return this.sessionDurations.filter(session => session.endTime && session.duration);
    }

    // Getter pour la session totale (toutes les sessions combinées)
    get totalSessionTime() {
        const completedDurations = this.completedSessions.map(s => s.duration || 0);
        const currentSessionDuration = this.currentSessionDuration;
        const currentDuration = currentSessionDuration ? currentSessionDuration.totalMs : 0;
        
        const totalMs = completedDurations.reduce((sum, duration) => sum + duration, currentDuration);
        
        const hours = Math.floor(totalMs / (1000 * 60 * 60));
        const minutes = Math.floor((totalMs % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((totalMs % (1000 * 60)) / 1000);
        
        return {
            totalMs,
            hours,
            minutes,
            seconds,
            formatted: `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`,
            sessionsCount: this.sessionDurations.length
        };
    }

    // Getter pour récupérer les infos de connexion formatées
    get loginInfo() {
        if (this.lastLoginIP) {
            const currentSession = this.currentSessionDuration;
            const firstSession = this.sessionDurations.length > 0 ? this.sessionDurations[0] : null;
            
            return {
                ip: this.lastLoginIP,
                currentSession: currentSession,
                totalTime: this.totalSessionTime,
                completedSessions: this.completedSessions.length,
                firstLoginDate: firstSession ? new Date(firstSession.startTime).toLocaleString('fr-FR', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit'
                }) : null,
                raw: {
                    ip: this.lastLoginIP,
                    currentTabId: currentSession ? currentSession.tabId : null,
                    firstSession: firstSession
                }
            };
        }
        return null;
    }

    @Action({ rawError: true })
    async login({ username, password }: LoginArg): Promise<void> {
        try {
            const resp = await login(username, password);
            const data = resp.data;

            // Enregistrer les informations de connexion
            await this.recordLoginInfo();

            this.context.commit('setToken', data.token);
            this.context.commit('setLogged', true);
            await this.getUser();
            location.reload();
        } catch (error) {
            throw error;
        }
    }

    @Action
    async loginWithToken(token: string): Promise<void> {
        try {
            // Enregistrer les informations de connexion
            await this.recordLoginInfo();

            this.context.commit('setToken', token);
            this.context.commit('setLogged', true);
            await this.getUser();
            location.reload();
        } catch (error) {
            throw error;
        }
    }

    // Mutation pour supprimer une session spécifique
    @Mutation
    removeSessionFromArray(tabId: string) {
        this.sessionDurations = this.sessionDurations.filter(s => s.tabId !== tabId);
    }

    // Action pour sauvegarder une session terminée en base
    @Action
    async saveSessionToDatabase(session: {
        tabId: string;
        startTime: string;
        endTime: string;
        duration: number;
    }): Promise<void> {
        try {
            const sessionData = {
                userId: this.userID,
                subscriptionId: this.subscriptions.length > 0 ? this.subscriptions[0].id : undefined,
                ipAddress: this.lastLoginIP || 'Unknown',
                tabId: session.tabId,
                startTime: session.startTime,
                endTime: session.endTime,
                duration: session.duration
            };

            const response = await saveTrackingConnection(sessionData);
            
            // Si la sauvegarde est réussie, supprimer la session du localStorage
            if (response.data && response.data.success) {
                this.context.commit('removeSessionFromArray', session.tabId);
                this.saveToLocalstorage(); // Sauvegarder immédiatement sans cette session
               
            }
        } catch (error) {
            console.warn('Could not save session to database:', error);
            // En cas d'erreur, garder la session en localStorage pour réessayer plus tard
        }
    }

    // Action pour enregistrer les infos de connexion
    @Action
    async recordLoginInfo(): Promise<void> {
        try {
            // Récupérer l'IP de l'utilisateur
            const userIP = await getUserIP();
            const now = new Date().toISOString();
            const tabId = 'tab_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
            
            // Terminer la session précédente s'il y en a une
            const currentSession = this.currentSession;
            if (currentSession) {
                const endTime = now;
                const startTime = new Date(currentSession.startTime);
                const duration = new Date(endTime).getTime() - startTime.getTime();
                
                // Mettre à jour la session précédente
                this.context.commit('updateSessionEnd', {
                    tabId: currentSession.tabId,
                    endTime: endTime,
                    duration: duration
                });
                
                // Sauvegarder la session terminée en base
                await this.saveSessionToDatabase({
                    tabId: currentSession.tabId,
                    startTime: currentSession.startTime,
                    endTime: endTime,
                    duration: duration
                });
            }
            
            // Sauvegarder dans le store
            this.context.commit('setLastLoginIP', userIP);
            
            // Créer une nouvelle session
            this.context.commit('addSessionDuration', {
                tabId: tabId,
                startTime: now,
                endTime: null,
                duration: null
            });
            
            // Configurer les événements de fermeture de l'onglet/navigateur
            this.setupTabCloseTracking();
            
           
            
        } catch (error) {
            console.warn('Could not record login info:', error);
        }
    }

    // Action spécialement pour les utilisateurs déjà connectés
    @Action
    async initExistingUserSession(): Promise<void> {
        try {
            let needUpdate = false;
            
            // Si on n'a pas d'IP, la récupérer
            if (!this.lastLoginIP) {
                const userIP = await getUserIP();
                this.context.commit('setLastLoginIP', userIP);
                needUpdate = true;
            }
            
            // Si on n'a pas de session active, la créer
            const currentSession = this.currentSession;
            if (!currentSession) {
                const tabId = 'tab_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
                const sessionStart = new Date().toISOString();
                
                this.context.commit('addSessionDuration', {
                    tabId: tabId,
                    startTime: sessionStart,
                    endTime: null,
                    duration: null
                });
                
                needUpdate = true;
                
            }
            
            // Toujours configurer le tracking de fermeture
            this.setupTabCloseTracking();
            
            if (needUpdate) {
                this.saveToLocalstorage();
            }
            
        } catch (error) {
            console.warn('Could not initialize existing user session:', error);
        }
    }

    // Action pour configurer le tracking de fermeture d'onglet
    @Action
    setupTabCloseTracking(): void {
        const handleTabClose = async () => {
            const currentSession = this.currentSession;
            if (currentSession) {
                const endTime = new Date().toISOString();
                const startTime = new Date(currentSession.startTime);
                const duration = new Date(endTime).getTime() - startTime.getTime();
                
                // Mettre à jour la session actuelle
                this.context.commit('updateSessionEnd', {
                    tabId: currentSession.tabId,
                    endTime: endTime,
                    duration: duration
                });
                
                // Sauvegarder immédiatement
                this.saveToLocalstorage();
                
                // Sauvegarder en base (peut ne pas fonctionner à la fermeture)
                try {
                    await this.saveSessionToDatabase({
                        tabId: currentSession.tabId,
                        startTime: currentSession.startTime,
                        endTime: endTime,
                        duration: duration
                    });
                } catch (error) {
                    console.warn('Could not save session on tab close:', error);
                }
                
              
            }
        };

        // Événements de fermeture
        window.addEventListener('beforeunload', handleTabClose);
        window.addEventListener('unload', handleTabClose);
        window.addEventListener('pagehide', handleTabClose);
        
        // Événement de focus/blur pour détecter le changement d'onglet
        document.addEventListener('visibilitychange', () => {
            if (document.hidden) {
                // L'onglet perd le focus, sauvegarder l'état actuel
                this.saveToLocalstorage();
            }
        });
    }

    // Helper pour formater la durée
    @Action
    formatDuration(ms: number): string {
        const hours = Math.floor(ms / (1000 * 60 * 60));
        const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((ms % (1000 * 60)) / 1000);
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }

    @Action
    async logout(): Promise<void> {
        // Terminer la session actuelle avant de se déconnecter
        const currentSession = this.currentSession;
        if (currentSession) {
            const endTime = new Date().toISOString();
            const startTime = new Date(currentSession.startTime);
            const duration = new Date(endTime).getTime() - startTime.getTime();
            
            this.context.commit('updateSessionEnd', {
                tabId: currentSession.tabId,
                endTime: endTime,
                duration: duration
            });

            // Sauvegarder la dernière session en base
            try {
                await this.saveSessionToDatabase({
                    tabId: currentSession.tabId,
                    startTime: currentSession.startTime,
                    endTime: endTime,
                    duration: duration
                });
            } catch (error) {
                console.warn('Could not save final session on logout:', error);
            }
        }

        this.context.commit('setLogged', false);
        this.context.commit('setToken', '');
        this.context.commit('setUserID', -1);
        this.context.commit('setRoles', []);
        this.context.commit('setEmail', '');
        this.context.commit('setFirstname', '');
        this.context.commit('setIsRestricted', false);
        this.context.commit('setLastname', '');
        this.context.commit('setLogo', '');
        this.context.commit('setTrialActive', null);
        this.context.commit('setSubscriptionActive', null);
        this.context.commit('setEnabledAt', null);
        this.context.commit('setSubscriptions', []);
        this.context.commit('setPersonalColor', null);
        this.context.commit('setUserTokens', []);
        this.context.commit('setCrmUrl', '');
        
        // Reset des infos de connexion
        this.context.commit('setLastLoginIP', null);
        this.context.commit('setSessionDurations', []);

        this.saveToLocalstorage();
        localStorage.removeItem('service');
        location.reload();
    }

    @Action
    async init(): Promise<void> {
        if (this.initialized) {
            return;
        }
        this.context.commit('setInitialized');

        this.restoreFromLocalstorage();
        if (this.logged) {
            await this.getUser();
            
            // Toujours initialiser la session pour les utilisateurs connectés
            await this.initExistingUserSession();
        }
    }

    @Action
    async getUser() {
        try {
            const resp = await getConnectedUser();
            const user = resp.data;
            const userID = user.id;
            const email = user.email;
            const firstname = user.firstname;
            const isRestricted = user.is_restricted;
            const lastname = user.lastname;
            const logo = user.logo_image;
            const roles = user.roles;
            const isKochise = user.is_kochise;
            const personal_color = user.personal_color;
            const userTokens = user.user_tokens;
            const crm_url = user.crm_url;
            
            this.context.commit('setIsKochise', isKochise);
            this.context.commit('setUserID', userID);
            this.context.commit('setRoles', roles);
            this.context.commit('setEmail', email);
            this.context.commit('setFirstname', firstname);
            this.context.commit('setIsRestricted', isRestricted);
            this.context.commit('setCrmUrl', crm_url);
            this.context.commit('setLastname', lastname);
            this.context.commit('setLogo', logo);
            this.context.commit(
                'setTrialActive',
                user.is_trial_active !== undefined ? user.is_trial_active : null
            );
            this.context.commit(
                'setSubscriptionActive',
                user.is_subscription_active !== undefined
                    ? user.is_subscription_active
                    : null
            );
            this.context.commit(
                'setEnabledAt',
                user.enabled_at ? new Date(user.enabled_at) : null
            );
            this.context.commit(
                'setSubscriptions',
                user.subscriptions ? user.subscriptions : []
            );
            this.context.commit('setPersonalColor', personal_color ? personal_color : '#891A2B');
            this.context.commit(
                'setUserTokens',
                user.user_tokens ? user.user_tokens : []
            );
            this.context.commit(
                'setCrmUrl',
                crm_url
            );

            this.saveToLocalstorage();
        } catch (error) {
            throw error;
        }
    }

    @Action
    saveToLocalstorage(): void {
        const state: AuthState = {
            isKochise: this.isKochise,
            logged: this.logged,
            token: this.token,
            roles: this.roles,
            userID: this.userID,
            firstname: this.firstname,
            isRestricted: this.isRestricted,
            lastname: this.lastname,
            logo: this.logo,
            email: this.email,
            isTrialActive: this.isTrialActive,
            isSubscriptionActive: this.isSubscriptionActive,
            enabledAt: this.enabledAt,
            subscriptions: this.subscriptions,
            personal_color: this.personal_color,
            userTokens: this.userTokens,
            crm_url: this.crm_url,
            // Nouvelles propriétés sauvegardées (currentTabId supprimé)
            lastLoginIP: this.lastLoginIP,
            sessionDurations: this.sessionDurations
        };
        localStorage.setItem('auth', JSON.stringify(state));
    }

    @Action
    restoreFromLocalstorage(): void {
        const stateStr = localStorage.getItem('auth');
        if (stateStr) {
            const state: AuthState = JSON.parse(stateStr);

            if (isTokenValid(state.token)) {
                this.context.commit('setIsKochise', state.isKochise);
                this.context.commit('setLogged', state.logged);
                this.context.commit('setToken', state.token);
                this.context.commit('setUserID', state.userID);
                this.context.commit('setRoles', state.roles);
                this.context.commit('setEmail', state.email);
                this.context.commit('setFirstname', state.firstname);
                this.context.commit('setIsRestricted', state.isRestricted);
                this.context.commit('setLastname', state.lastname);
                this.context.commit('setLogo', state.logo);
                this.context.commit('setTrialActive', state.isTrialActive);
                this.context.commit(
                    'setSubscriptionActive',
                    state.isSubscriptionActive
                );
                this.context.commit('setEnabledAt', state.enabledAt);
                this.context.commit('setSubscriptions', state.subscriptions);
                this.context.commit('setPersonalColor', state.personal_color);
                this.context.commit('setUserTokens', state.userTokens);
                this.context.commit('setCrmUrl', state.crm_url);
                
                // Restaurer les nouvelles propriétés (currentTabId supprimé)
                this.context.commit('setLastLoginIP', state.lastLoginIP || null);
                this.context.commit('setSessionDurations', state.sessionDurations || []);
            } else {
                this.saveToLocalstorage();
            }
        }
    }
}

export default getModule(AuthModule);